import { useState, useEffect } from 'react'
import { X, Search, Calendar, Phone, User, Clock, Loader2, AlertTriangle } from 'lucide-react'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

interface Appointment {
  id: string
  service: string
  date: string
  time: string
  duration: number
  status: string
  customer_name: string
  stylist_name: string
  price: number
}

interface CancellationModalProps {
  isOpen: boolean
  customerPhone: string | null
  customerName: string | null
  onClose: () => void
  onNavigateToCalendar?: () => void
}

export function CancellationModal({
  isOpen,
  customerPhone,
  customerName,
  onClose,
  onNavigateToCalendar
}: CancellationModalProps) {
  const [searchPhone, setSearchPhone] = useState(customerPhone || '')
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [cancelledIds, setCancelledIds] = useState<Set<string>>(new Set())

  // Auto-search when phone is provided
  useEffect(() => {
    if (customerPhone && isOpen) {
      setSearchPhone(customerPhone)
      searchAppointments(customerPhone)
    }
  }, [customerPhone, isOpen])

  const searchAppointments = async (phone: string) => {
    if (!phone || phone.length < 5) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      // Search by phone - get upcoming appointments
      const response = await fetch(`${API_URL}/api/customers/phone/${encodeURIComponent(phone)}`)
      const customerData = await response.json()
      
      if (customerData.error || !customerData.id) {
        setError('No customer found with this phone number')
        setAppointments([])
        return
      }
      
      // Get appointments for this customer
      const today = new Date().toISOString().split('T')[0]
      const futureDate = new Date()
      futureDate.setMonth(futureDate.getMonth() + 3)
      const endDate = futureDate.toISOString().split('T')[0]
      
      const aptsResponse = await fetch(
        `${API_URL}/api/appointments/range?startDate=${today}&endDate=${endDate}`
      )
      const aptsData = await aptsResponse.json()
      
      // Filter to this customer's upcoming non-cancelled appointments
      const customerApts = (aptsData.appointments || []).filter((apt: any) => 
        apt.customer_id === customerData.id && 
        apt.status !== 'cancelled' &&
        apt.date >= today
      )
      
      setAppointments(customerApts)
      
      if (customerApts.length === 0) {
        setError('No upcoming appointments found for this customer')
      }
    } catch (err) {
      console.error('Search error:', err)
      setError('Failed to search appointments')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelAppointment = async (apt: Appointment) => {
    if (!confirm(`Cancel ${apt.service} on ${formatDate(apt.date)} at ${apt.time}?`)) {
      return
    }
    
    setCancellingId(apt.id)
    
    try {
      const response = await fetch(`${API_URL}/api/appointments/${apt.id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          notes: 'Cancelled via phone call'
        })
      })
      
      if (response.ok) {
        setCancelledIds(prev => new Set([...prev, apt.id]))
        // Remove from list
        setAppointments(prev => prev.filter(a => a.id !== apt.id))
      } else {
        alert('Failed to cancel appointment')
      }
    } catch (err) {
      console.error('Cancel error:', err)
      alert('Failed to cancel appointment')
    } finally {
      setCancellingId(null)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl animate-in fade-in zoom-in duration-200 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 p-4 rounded-t-xl flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-900">
                Cancellation Request Detected
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Customer wants to cancel an appointment
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Search by Phone Number
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                placeholder="Enter phone number..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <button
              onClick={() => searchAppointments(searchPhone)}
              disabled={isLoading || searchPhone.length < 5}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </button>
          </div>
          
          {customerName && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span>Customer: <strong>{customerName}</strong></span>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{error}</p>
            </div>
          )}
          
          {appointments.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-3">
                Found {appointments.length} upcoming appointment{appointments.length !== 1 ? 's' : ''}:
              </p>
              
              {appointments.map((apt) => (
                <div 
                  key={apt.id}
                  className={`
                    border rounded-lg p-4 transition-all
                    ${cancelledIds.has(apt.id) 
                      ? 'bg-gray-100 border-gray-200 opacity-50' 
                      : 'bg-white border-gray-200 hover:border-amber-300 hover:shadow-sm'}
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{apt.service}</h4>
                      <div className="mt-1 space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(apt.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{apt.time} ({apt.duration} mins)</span>
                        </div>
                        {apt.stylist_name && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>with {apt.stylist_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleCancelAppointment(apt)}
                      disabled={cancellingId === apt.id || cancelledIds.has(apt.id)}
                      className={`
                        px-3 py-2 rounded-lg text-sm font-medium transition-colors
                        ${cancelledIds.has(apt.id)
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'}
                      `}
                    >
                      {cancellingId === apt.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : cancelledIds.has(apt.id) ? (
                        'Cancelled'
                      ) : (
                        'Cancel'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {!isLoading && !error && appointments.length === 0 && !searchPhone && (
            <div className="text-center py-8 text-gray-500">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Enter a phone number to search for appointments</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-xl flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              Continue Call
            </button>
            {onNavigateToCalendar && (
              <button
                onClick={onNavigateToCalendar}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Open Calendar
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center">
            The call transcript continues in the background
          </p>
        </div>
      </div>
    </div>
  )
}

export type { Appointment }
